import type {
  PrintBackend,
  PrintDispatcherOptions,
  PrintFailure,
  PrintJob,
  PrintOutcome,
} from '@/models/FulfillmentPrint';

/**
 * How long a print frame stays once its print is handed over: long enough for anyone to finish with Chrome's
 * print dialog, which a pdf frame's print() does not wait on, and well past the silent-print queue taking every
 * page (10/14, kiosk Chrome: closed at once nothing reached the queue). The frame is gone before the next shift
 * notices it.
 */
const FRAME_LIFETIME_MS = 10 * 60_000;

/** How long the next sheet waits on a carrier pdf that never loads in its frame. */
const PDF_LOAD_WAIT_MS = 15_000;
/** How often the pdf frame is checked for the pdf having loaded. */
const PDF_LOAD_POLL_MS = 100;

/**
 * The browser as the one printer: every sheet prints at once from the page. On a PC whose Chrome has the
 * silent-print policy it goes straight to the printer that policy names; anywhere else Chrome's own print
 * dialog opens over the page [10/16].
 */
export class BrowserPrintBackend implements PrintBackend {
  public async print(job: PrintJob): Promise<void> {
    if (job.format === 'dom') {
      await this.printDom(job);
      return;
    }
    if (job.format === 'html') {
      this.printHtml(job);
      return;
    }
    if (!job.pdfBlob) {
      throw new Error('The print job carried no pdf.');
    }
    await this.printPdfInFrame(job.pdfBlob);
  }

  /** Isolation is by body class, removed immediately because `afterprint` may never fire. */
  private async printDom(job: PrintJob): Promise<void> {
    if (job.domPrint) {
      // Wait for react-to-print's Deleo-family sticker so the next sheet does not print first.
      await job.domPrint();
      return;
    }
    const surfaceClass = job.printSurfaceClass;
    if (!surfaceClass) {
      throw new Error('This surface named no print stylesheet to isolate it.');
    }
    document.body.classList.add(surfaceClass);
    const restore = () => document.body.classList.remove(surfaceClass);
    window.addEventListener('afterprint', restore, { once: true });
    try {
      window.print();
    } finally {
      restore();
    }
  }

  /**
   * A frame rather than a tab: one Print click can carry several sheets, and a tab would come to the front at
   * every Print and stay behind after it. The frame is a document of its own, so the sheet's @page still sizes the paper.
   */
  private printHtml(job: PrintJob): void {
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('title', `${job.surface} print sheet`);
    // Off screen rather than hidden: a frame the browser treats as invisible can print blank.
    frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:0;height:0;border:0;';
    document.body.appendChild(frame);
    const sheet = frame.contentWindow;
    if (!sheet) {
      frame.remove();
      throw new Error('The print sheet could not be prepared.');
    }
    // `afterprint` may never fire, so the frame also goes once nobody can still be printing from it.
    const remove = () => frame.remove();
    sheet.addEventListener('afterprint', remove, { once: true });
    window.setTimeout(remove, FRAME_LIFETIME_MS);
    sheet.document.open();
    sheet.document.write(job.html ?? '');
    sheet.document.close();
    sheet.print();
  }

  /**
   * The pdf printed from a frame on this page, which stays until the printer has surely taken every page.
   * Settles once the frame has printed the pdf, so the next sheet on the same click comes out after it;
   * rejects when the frame refused to print or never showed the pdf within the load wait.
   */
  private printPdfInFrame(pdfBlob: Blob): Promise<void> {
    const url = URL.createObjectURL(pdfBlob);
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('title', 'label print sheet');
    // Off screen rather than hidden, as for the html sheets.
    frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:0;height:0;border:0;';
    document.body.appendChild(frame);
    const drop = () => {
      frame.remove();
      URL.revokeObjectURL(url);
    };
    window.setTimeout(drop, FRAME_LIFETIME_MS);
    return new Promise<void>((resolve, reject) => {
      const loaded = (): Window | null => {
        try {
          const sheet = frame.contentWindow;
          return sheet && sheet.location.href === url && sheet.document.readyState === 'complete' ? sheet : null;
        } catch {
          return null;
        }
      };
      let watch = 0;
      let giveUp = 0;
      const stop = () => {
        window.clearInterval(watch);
        window.clearTimeout(giveUp);
      };
      watch = window.setInterval(() => {
        const sheet = loaded();
        if (!sheet) {
          return;
        }
        stop();
        try {
          sheet.print();
          resolve();
        } catch (cause) {
          reject(cause);
        }
      }, PDF_LOAD_POLL_MS);
      giveUp = window.setTimeout(() => {
        stop();
        drop();
        reject(new Error('The label sheet did not load in time.'));
      }, PDF_LOAD_WAIT_MS);
      frame.src = url;
    });
  }
}

/** Every print on the fulfillment desk goes through here, so each sheet succeeds or fails in the same words. */
export class PrintDispatcher {
  constructor(private readonly options: PrintDispatcherOptions = {}) {}

  public async dispatch(job: PrintJob): Promise<PrintOutcome> {
    const [outcome] = await this.dispatchAll([job]);
    return outcome!;
  }

  /** Several sheets for one click, in the order given; each succeeds or fails on its own. */
  public async dispatchAll(jobs: PrintJob[]): Promise<PrintOutcome[]> {
    const outcomes: PrintOutcome[] = [];
    for (const job of jobs) {
      outcomes.push(await this.printOne(job));
    }
    return outcomes;
  }

  private async printOne(job: PrintJob): Promise<PrintOutcome> {
    const backend = this.options.backend ?? new BrowserPrintBackend();
    try {
      await backend.print(job);
      return { ok: true };
    } catch (cause) {
      return {
        ok: false,
        printer: job.role,
        reason: cause instanceof Error ? cause.message : String(cause),
      };
    }
  }
}

/** One wording for a refused print, so every screen's toast reads the same at the same desk. */
export class PrintFailureNotice {
  public static textOf(failure: PrintFailure): string {
    return `Print failed on ${failure.printer}: ${failure.reason}`;
  }
}
